import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import { parseSpreadsheetMlRows } from './OrganizationsBulkImportModal';
import { PO_ITEM_TYPE_ORDER } from './purchaseOrderSummary';

const TEMPLATE_HEADERS = [
  'po_code',
  'item_type',
  'item_code',
  'description',
  'detail',
  'quantity',
  'unit',
  'format_code',
  'unit_price',
];

const COLUMN_LABELS = {
  po_code: 'O.C',
  item_type: 'Tipo',
  item_code: 'Código',
  description: 'Descripción',
  detail: 'Detalle',
  quantity: 'Cantidad',
  unit: 'Unidad',
  format_code: 'Formato',
  unit_price: 'Precio unitario',
};

const TEMPLATE_EXAMPLE_CELLS = [
  '25309',
  'Insumos',
  '',
  'DESINFECTANTE A/SOL IGENIX 480 CC',
  '',
  '1',
  'UN',
  '',
  '2245',
];

const HEADER_ALIASES = {
  po_code: [
    'po_code',
    'oc',
    'o_c',
    'orden',
    'orden_de_compra',
    'orden_compra',
    'n_oc',
    'nro_oc',
    'numero_oc',
    'codigo_oc',
    'n_orden',
  ],
  item_type: ['item_type', 'tipo', 'tipo_item', 'tipo_de_item'],
  item_code: ['item_code', 'codigo', 'sku', 'codigo_item', 'codigo_producto'],
  description: ['description', 'descripcion', 'producto', 'nombre', 'item', 'nombre_item'],
  detail: ['detail', 'detalle', 'observaciones', 'obs', 'comentario'],
  quantity: ['quantity', 'cantidad', 'qty', 'cant'],
  unit: ['unit', 'unidad', 'ud', 'um'],
  format_code: ['format_code', 'formato', 'format'],
  unit_price: ['unit_price', 'precio', 'precio_unitario', 'valor', 'p_unitario', 'precio_u'],
};

const TYPE_ALIASES = {
  materiaprima: 'Materias primas',
  materiasprimas: 'Materias primas',
  mp: 'Materias primas',
  insumo: 'Insumos',
  insumos: 'Insumos',
  upselling: 'Upselling',
  upsell: 'Upselling',
  servicio: 'Servicios',
  servicios: 'Servicios',
  transporte: 'Transporte',
  flete: 'Transporte',
  despacho: 'Transporte',
  otro: 'Otros',
  otros: 'Otros',
};

const emptyRow = () =>
  TEMPLATE_HEADERS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const downloadPurchaseOrderItemsExcelTemplate = () => {
  const headerCells = TEMPLATE_HEADERS.map(
    (header) => `<Cell><Data ss:Type="String">${escapeXml(COLUMN_LABELS[header] || header)}</Data></Cell>`
  ).join('');
  const exampleCells = TEMPLATE_EXAMPLE_CELLS.map(
    (cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
  ).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Items OC">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_items_ordenes_compra.xls';
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const foldName = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');

const normalizePoCodeValue = (value) => {
  const raw = String(value ?? '').trim();
  if (/^\d+\.0+$/.test(raw)) return raw.split('.')[0];
  return raw;
};

const mapHeaderToField = (header) => {
  const key = normalizeHeader(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(key)) return field;
  }
  return TEMPLATE_HEADERS.includes(key) ? key : null;
};

const looksLikeSpreadsheetMl = (text = '') => {
  const sample = String(text || '').slice(0, 4000).toLowerCase();
  return (
    sample.includes('mso-application') ||
    sample.includes('urn:schemas-microsoft-com:office:spreadsheet') ||
    (sample.includes('<workbook') && sample.includes('<worksheet'))
  );
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '');
  if (!sample) return false;
  if (sample.charCodeAt(0) === 0x50 && sample.charCodeAt(1) === 0x4b) return true;
  const nulls = [...sample.slice(0, 2000)].filter((ch) => ch.charCodeAt(0) === 0).length;
  return nulls > 20;
};

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis >= commas) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const buildRowsFromCellTable = (table = []) => {
  if (!table.length) return { rows: [], error: 'No se encontraron filas.' };
  const mapping = (table[0] || []).map(mapHeaderToField);
  if (!mapping.includes('po_code')) {
    return { rows: [], error: 'Falta la columna de O.C. (código de orden de compra).' };
  }
  const rows = [];
  for (let i = 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    const row = emptyRow();
    mapping.forEach((field, idx) => {
      if (!field) return;
      row[field] = String(cells[idx] ?? '').trim();
    });
    if (row.po_code && (row.description || row.item_code)) rows.push(row);
  }
  if (!rows.length) {
    return { rows: [], error: 'No se encontraron filas de ítems con O.C. y descripción o código.' };
  }
  return { rows, error: '' };
};

export const parsePurchaseOrderItemsCsv = (rawText = '') => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV / Excel.' };
  if (looksLikeBinaryOffice(text)) return { rows: [], error: 'xlsx' };
  if (looksLikeSpreadsheetMl(text)) return buildRowsFromCellTable(parseSpreadsheetMlRows(text));
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], error: 'El archivo está vacío.' };
  const delimiter = detectDelimiter(lines[0]);
  return buildRowsFromCellTable(lines.map((line) => splitCsvLine(line, delimiter)));
};

const homologateItemType = (raw, itemTypeOptions = []) => {
  const folded = foldName(raw);
  if (!folded) return '';
  const labels = [
    ...PO_ITEM_TYPE_ORDER,
    ...(itemTypeOptions || []).map((opt) => opt.label || opt.value).filter(Boolean),
  ];
  const hit = labels.find((label) => foldName(label) === folded);
  if (hit) return hit;
  return TYPE_ALIASES[folded] || String(raw || '').trim();
};

const homologateImportRow = (row, { orders = [], catalogProducts = [], itemTypeOptions = [] } = {}) => {
  const next = { ...emptyRow(), ...row };
  const poCode = normalizePoCodeValue(next.po_code);
  next.po_code = poCode;
  const order = (orders || []).find(
    (item) => String(item.po_code || '').trim().toLowerCase() === poCode.toLowerCase()
  );
  if (order) next.po_code = order.po_code;
  next.item_type = homologateItemType(next.item_type, itemTypeOptions);
  const code = String(next.item_code || '').trim().toUpperCase();
  const catalog = code
    ? (catalogProducts || []).find((item) => String(item.item_code || '').trim().toUpperCase() === code)
    : null;
  if (catalog) {
    if (!next.item_type) next.item_type = catalog.item_type || '';
    if (!next.description) next.description = catalog.description || '';
    if (!next.unit) next.unit = catalog.unit || 'UN';
    if (!next.format_code) next.format_code = catalog.format_code || '';
    if (!next.unit_price) next.unit_price = catalog.unit_price ?? '';
  }
  if (!next.unit) next.unit = 'UN';
  if (!next.quantity) next.quantity = '1';
  return next;
};

const getRowErrors = (row, orders = []) => {
  const errors = {};
  const poCode = normalizePoCodeValue(row.po_code);
  if (!poCode) errors.po_code = 'Obligatorio';
  else if (
    !(orders || []).some((item) => String(item.po_code || '').trim().toLowerCase() === poCode.toLowerCase())
  ) {
    errors.po_code = 'OC no existe';
  }
  if (!String(row.description || '').trim() && !String(row.item_code || '').trim()) {
    errors.description = 'Descripción o código';
  }
  return errors;
};

const toApiPayload = (row) => ({
  po_code: normalizePoCodeValue(row.po_code),
  item_type: String(row.item_type || '').trim(),
  item_code: String(row.item_code || '').trim(),
  description: String(row.description || '').trim(),
  detail: String(row.detail || '').trim(),
  quantity: String(row.quantity || '').trim() || 1,
  unit: String(row.unit || 'UN').trim() || 'UN',
  format_code: String(row.format_code || '').trim(),
  unit_price: String(row.unit_price || '').trim() || 0,
});

const PurchaseOrderItemsBulkImportModal = ({
  isOpen = false,
  onClose,
  onImport,
  isImporting = false,
  isDarkMode = false,
  result = null,
  orders = [],
  catalogProducts = [],
  itemTypeOptions = [],
}) => {
  const [step, setStep] = useState('source');
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isParsingFile, setIsParsingFile] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('source');
    setRawText('');
    setPreviewRows([]);
    setLocalError('');
    setLocalSuccess('');
    setIsParsingFile(false);
  }, [isOpen]);

  const colors = {
    text: isDarkMode ? '#f4f4f5' : '#0f172a',
    muted: isDarkMode ? '#a1a1aa' : '#64748b',
    border: isDarkMode ? '#3f3f46' : '#e2e8f0',
    inputBg: isDarkMode ? '#18181b' : '#fff',
    inputBorder: isDarkMode ? '#3f3f46' : '#cbd5e1',
    panel: isDarkMode ? '#09090b' : '#fff',
  };

  const parsed = useMemo(() => parsePurchaseOrderItemsCsv(rawText), [rawText]);
  const rowErrors = useMemo(
    () => previewRows.map((row) => getRowErrors(row, orders)),
    [previewRows, orders]
  );
  const invalidFieldCount = rowErrors.reduce((sum, err) => sum + Object.keys(err).length, 0);

  if (!isOpen) return null;

  const applyParsedRows = (rows) => {
    setPreviewRows(
      rows.map((row) => homologateImportRow({ ...emptyRow(), ...row }, { orders, catalogProducts, itemTypeOptions }))
    );
    setStep('preview');
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLocalError('');
    setLocalSuccess('');
    const name = String(file.name || '').toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xlsm') || file.type.includes('spreadsheetml');
    try {
      if (isXlsx || looksLikeBinaryOffice(await file.slice(0, 8).text().catch(() => ''))) {
        setIsParsingFile(true);
        const data = await purchaseOrdersAPI.parseBulkItemsFile(file);
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!rows.length) {
          setLocalError('No se encontraron filas de ítems.');
          return;
        }
        applyParsedRows(rows);
        return;
      }
      const text = await file.text();
      if (looksLikeBinaryOffice(text)) {
        setIsParsingFile(true);
        const data = await purchaseOrdersAPI.parseBulkItemsFile(file);
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!rows.length) {
          setLocalError('No se encontraron filas de ítems.');
          return;
        }
        applyParsedRows(rows);
        return;
      }
      setRawText(text);
    } catch (err) {
      setLocalError(err?.message || 'No se pudo leer el archivo.');
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleContinueFromSource = () => {
    if (parsed.error || !parsed.rows.length) {
      setLocalError(parsed.error || 'No hay filas para revisar.');
      return;
    }
    applyParsedRows(parsed.rows);
    setLocalError('');
    setLocalSuccess('');
  };

  const handleSubmit = async () => {
    if (!previewRows.length || invalidFieldCount > 0 || isImporting || localSuccess) return;
    setLocalError('');
    try {
      const data = await onImport?.(previewRows.map(toApiPayload));
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      const ordersUpdated = Number(data?.orders_updated || 0);
      if (errors === 0 || created > 0) {
        setLocalSuccess(
          `Importación de ítems: ${created} línea(s) en ${ordersUpdated} OC` +
            (skipped ? `, ${skipped} omitidas` : '') +
            '.'
        );
      }
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !isImporting && !isParsingFile && onClose?.()}
    >
      <div
        style={{
          width: 'min(1180px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 16,
          display: 'grid',
          gap: 12,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
              {step === 'preview' ? 'Revisar ítems' : 'Carga masiva de ítems de OC'}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>
              {step === 'preview'
                ? 'Las líneas se agregan a OC existentes. El código de O.C. debe coincidir con una orden ya creada.'
                : 'Sube la planilla de ítems (O.C, tipo, código, descripción, cantidad, unidad y precio).'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting || isParsingFile}
            style={{ border: 'none', background: 'transparent', color: colors.muted, cursor: 'pointer', padding: 4 }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isParsingFile ? 'wait' : 'pointer',
                }}
              >
                <Upload size={15} />
                {isParsingFile ? 'Leyendo archivo…' : 'Subir Excel / CSV'}
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx,.xlsm,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  disabled={isParsingFile}
                  onChange={handleFile}
                />
              </label>
              <button
                type="button"
                onClick={downloadPurchaseOrderItemsExcelTemplate}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Descargar plantilla Excel
              </button>
            </div>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                setLocalError('');
              }}
              placeholder={`O.C;Tipo;Código;Descripción;Detalle;Cantidad;Unidad;Formato;Precio unitario\n${TEMPLATE_EXAMPLE_CELLS.join(';')}`}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 12, color: colors.muted }}>
              {isParsingFile
                ? 'Procesando el Excel…'
                : parsed.error === 'xlsx'
                  ? 'Usa el botón Subir para archivos .xlsx.'
                  : parsed.error
                    ? parsed.error
                    : `${parsed.rows.length} fila(s) detectadas. Continúa para revisarlas.`}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: colors.muted }}>
              <span>
                {previewRows.length} fila(s)
                {invalidFieldCount > 0 ? ` · ${invalidFieldCount} campo(s) con error` : ' · formato OK'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setStep('source');
                  setLocalError('');
                }}
                disabled={isImporting}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.text,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                }}
              >
                Volver al archivo
              </button>
            </div>
            <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8, maxHeight: '58vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1080 }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>#</th>
                    {TEMPLATE_HEADERS.map((key) => (
                      <th key={key} style={{ padding: '8px 10px', position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>
                        {COLUMN_LABELS[key]}
                      </th>
                    ))}
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }} />
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    return (
                      <tr key={`item-row-${index}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '6px 10px', color: colors.muted }}>{index + 1}</td>
                        {TEMPLATE_HEADERS.map((key) => {
                          const fieldError = errors[key];
                          return (
                            <td key={key} style={{ padding: '4px 6px' }}>
                              <input
                                value={row[key] ?? ''}
                                onChange={(e) =>
                                  setPreviewRows((prev) =>
                                    prev.map((item, i) => (i === index ? { ...item, [key]: e.target.value } : item))
                                  )
                                }
                                aria-invalid={Boolean(fieldError)}
                                style={{
                                  width: key === 'description' || key === 'detail' ? 160 : 96,
                                  height: 30,
                                  borderRadius: 6,
                                  border: `1px solid ${fieldError ? '#dc2626' : colors.inputBorder}`,
                                  background: fieldError
                                    ? isDarkMode
                                      ? '#450a0a'
                                      : '#fef2f2'
                                    : colors.inputBg,
                                  color: fieldError ? (isDarkMode ? '#fecaca' : '#991b1b') : colors.text,
                                  padding: '0 8px',
                                  fontSize: 12,
                                  boxSizing: 'border-box',
                                }}
                                title={fieldError || ''}
                              />
                            </td>
                          );
                        })}
                        <td style={{ padding: '4px 8px' }}>
                          <button
                            type="button"
                            onClick={() => setPreviewRows((prev) => prev.filter((_, i) => i !== index))}
                            disabled={isImporting}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.muted,
                              cursor: 'pointer',
                              padding: 4,
                              display: 'inline-flex',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {(localError || localSuccess || result) && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              color: colors.text,
              background: isDarkMode ? '#09090b' : '#f8fafc',
            }}
          >
            {localSuccess ? (
              <div style={{ color: isDarkMode ? '#86efac' : '#15803d', fontWeight: 600 }}>{localSuccess}</div>
            ) : null}
            {localError ? <div style={{ color: '#dc2626' }}>{localError}</div> : null}
            {!localSuccess && result ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <div>
                  Creadas: <strong>{result.created || 0}</strong> · OC actualizadas:{' '}
                  <strong>{result.orders_updated || 0}</strong> · Errores:{' '}
                  <strong>{result.errors || 0}</strong>
                </div>
                {Array.isArray(result.error_rows) && result.error_rows.length > 0 ? (
                  <div style={{ color: '#dc2626', fontSize: 12 }}>
                    {result.error_rows.slice(0, 6).map((row) => (
                      <div key={`err-${row.row}`}>
                        Fila {row.row}
                        {row.po_code ? ` (${row.po_code})` : ''}: {row.detail}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting || isParsingFile}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.inputBorder}`,
              background: 'transparent',
              color: colors.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {step === 'source' ? (
            <button
              type="button"
              onClick={handleContinueFromSource}
              disabled={Boolean(parsed.error) || !parsed.rows.length || isParsingFile}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: !parsed.rows.length || parsed.error || isParsingFile ? '#94a3b8' : '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: !parsed.rows.length || parsed.error || isParsingFile ? 'not-allowed' : 'pointer',
              }}
            >
              Revisar datos
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isImporting || Boolean(localSuccess) || invalidFieldCount > 0 || !previewRows.length}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? '#86efac'
                    : '#16a34a',
                color: '#fff',
                fontWeight: 600,
                cursor:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isImporting ? 'Importando…' : localSuccess ? 'Listo' : `Importar ${previewRows.length || ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderItemsBulkImportModal;
